import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import Swal from "sweetalert2";

import { Site, updateSiteOrder } from "../../api/sites";
import { appVersion, serverApi } from "../../api/http";
import SiteNavigator from "./SiteNavigator";
import PathConverter from "./PathConverter";
import PathUpdateModal from "../pathModal/PathUpdateModal";
import SshProfileManagerModal from "../pathModal/SshProfileManagerModal";
import IntroGuideModal, { INTRO_GUIDE_CONTENT_VERSION } from "../guide/IntroGuideModal";
import DeploymentHistoryModal from "../deployment/DeploymentHistoryModal";
import SshTerminalWorkspace, {
    SshTerminalSession,
    TerminalSession,
} from "../deployment/SshTerminalWorkspace";
import { requestTransferCredentials } from "../deployment/transferCredentials";
import { styles } from "../../styles/ConverterStyles";
import deployKitIcon from "../../assets/brand/deploykit-icon.png";
import { checkForAppUpdate, downloadAppUpdateInstaller, UpdateCheckResult } from "../../api/updateCheck";

const INTRO_GUIDE_STORAGE_KEY = "deployProjectIntroGuideSeen";
const UPDATE_DISMISSED_VERSION_STORAGE_KEY = "deployKitUpdateDismissedVersion";
const SUPPORT_GITHUB_URL = "https://github.com/Backjinuk/DeployKit-Desktop/issues";
const SUPPORT_EMAIL = "backj123@naver.com";

type ServerConnectionField =
    | "sshHost"
    | "sshPort"
    | "sshUsername"
    | "sshAuthType"
    | "sshPrivateKeyPath"
    | "remoteUploadPath"
    | "strictHostKeyChecking";
type PathBackupSite = Pick<
    Site,
    "id" | "sort" | "text" | "homePath" | "localPath" | "jdkPath" | "useYn" | ServerConnectionField
>;
type ImportedPathSite = Pick<Site, "text" | "homePath" | "localPath"> &
    Pick<Partial<Site>, "sort" | "jdkPath" | ServerConnectionField>;
type PathIdentitySite = Pick<Site, "text" | "homePath" | "localPath">;
type ThemeMode = "light" | "dark";
type WorkspacePage = "package" | "terminal";
type SupportLogExportResponse = {
    fileName: string;
    path: string;
    size: number;
    logFileCount: number;
};
type IntroGuidePreferenceResponse = {
    guideVersion?: string | null;
};

type ConverterMainProps = {
    themeMode: ThemeMode;
    onToggleThemeMode: () => void;
};

const timestampForFileName = () => {
    const now = new Date();
    const pad = (value: number) => value.toString().padStart(2, "0");

    return [
        now.getFullYear(),
        pad(now.getMonth() + 1),
        pad(now.getDate()),
        "-",
        pad(now.getHours()),
        pad(now.getMinutes()),
        pad(now.getSeconds()),
    ].join("");
};

const toPathBackupSite = (site: Site): PathBackupSite => ({
    id: site.id,
    sort: site.sort,
    text: site.text,
    homePath: site.homePath,
    localPath: site.localPath,
    jdkPath: site.jdkPath,
    sshHost: site.sshHost,
    sshPort: site.sshPort,
    sshUsername: site.sshUsername,
    sshAuthType: site.sshAuthType,
    sshPrivateKeyPath: site.sshPrivateKeyPath,
    remoteUploadPath: site.remoteUploadPath,
    strictHostKeyChecking: site.strictHostKeyChecking,
    useYn: site.useYn,
});

const isObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null;

const stringValue = (value: unknown) => (typeof value === "string" ? value.trim() : "");
const numberValue = (value: unknown) =>
    typeof value === "number" && Number.isFinite(value)
        ? value
        : typeof value === "string" && Number.isFinite(Number(value))
          ? Number(value)
          : undefined;
const booleanValue = (value: unknown) =>
    typeof value === "boolean"
        ? value
        : value === "true"
          ? true
          : value === "false"
            ? false
            : undefined;

const normalizePathPart = (value: string) =>
    value.trim().replace(/\\/g, "/").replace(/\/+$/, "").toLowerCase();

const pathIdentity = (site: PathIdentitySite) =>
    JSON.stringify([
        normalizePathPart(site.text),
        normalizePathPart(site.homePath),
        normalizePathPart(site.localPath),
    ]);

const uniquePathSites = <T extends PathIdentitySite>(pathList: T[]): T[] => {
    const seen = new Set<string>();
    return pathList.filter((site) => {
        const key = pathIdentity(site);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

const requestLocalAppShutdown = async () => {
    const url = "/api/app/shutdown";
    if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([], { type: "application/json" }));
        return;
    }

    await fetch(url, { method: "POST", keepalive: true }).catch(() => undefined);
};

const updateDownloadErrorMessage = (error: unknown) => {
    const response = (error as { response?: { status?: number; data?: unknown } })?.response;
    const status = response?.status;
    const responseData = response?.data;
    const serverMessage = isObject(responseData)
        ? stringValue(responseData.message) || stringValue(responseData.detail)
        : "";

    if (serverMessage && !["Conflict", "Bad Gateway"].includes(serverMessage)) {
        return serverMessage;
    }

    if (status === 409) {
        return "업데이트 설치 파일을 저장할 수 없습니다. 열려 있는 설치 파일이나 다운로드 폴더 미리보기를 닫고 다시 시도해 주세요.";
    }

    if (status === 502) {
        return "다운로드한 설치 파일이 정상 EXE가 아닙니다. 배포 서버에서 Git LFS 실제 파일을 받은 뒤 다시 배포해 주세요.";
    }

    return "네트워크 상태와 배포 서버의 설치 파일을 확인한 뒤 다시 시도해 주세요.";
};

const markIntroGuideVersionSeen = async () => {
    localStorage.setItem(INTRO_GUIDE_STORAGE_KEY, INTRO_GUIDE_CONTENT_VERSION);
    await serverApi
        .put("/api/preferences/intro-guide", { guideVersion: INTRO_GUIDE_CONTENT_VERSION })
        .catch((error) => console.info("intro guide preference save failed:", error));
};

const extractPathEntries = (value: unknown): unknown[] => {
    if (Array.isArray(value)) return value;
    if (isObject(value) && Array.isArray(value.sites)) return value.sites;
    throw new Error("Invalid path backup file.");
};

const parseImportedPaths = (value: unknown): ImportedPathSite[] => {
    return extractPathEntries(value).reduce<ImportedPathSite[]>((sites, entry) => {
        if (!isObject(entry)) return sites;
        if (stringValue(entry.useYn).toUpperCase() === "N") return sites;

        const text = stringValue(entry.text);
        const sort = numberValue(entry.sort);
        const homePath = stringValue(entry.homePath);
        const localPath = stringValue(entry.localPath);
        const jdkPath = stringValue(entry.jdkPath);
        const sshHost = stringValue(entry.sshHost);
        const sshPort = numberValue(entry.sshPort);
        const sshUsername = stringValue(entry.sshUsername);
        const sshAuthType = stringValue(entry.sshAuthType);
        const sshPrivateKeyPath = stringValue(entry.sshPrivateKeyPath);
        const remoteUploadPath = stringValue(entry.remoteUploadPath);
        const strictHostKeyChecking = booleanValue(entry.strictHostKeyChecking);

        if (!text || !homePath || !localPath) return sites;

        sites.push({
            text,
            sort: sort != null && Number.isInteger(sort) && sort >= 0 ? sort : undefined,
            homePath,
            localPath,
            jdkPath: jdkPath || undefined,
            sshHost: sshHost || undefined,
            sshPort,
            sshUsername: sshUsername || undefined,
            sshAuthType:
                sshAuthType === "PRIVATE_KEY" || sshAuthType === "PASSWORD"
                    ? sshAuthType
                    : undefined,
            sshPrivateKeyPath: sshPrivateKeyPath || undefined,
            remoteUploadPath: remoteUploadPath || undefined,
            strictHostKeyChecking,
        });
        return sites;
    }, []);
};

const ConverterMain: React.FC<ConverterMainProps> = ({ themeMode, onToggleThemeMode }) => {
    const [sites, setSites] = useState<Site[]>([]);
    const [selected, setSelected] = useState<Site | null>(null);
    const [showPathUpdateModal, setShowPathUpdateModal] = useState<boolean>(false);
    const [pathManageCreateMode, setPathManageCreateMode] = useState(false);
    const [showSshProfileModal, setShowSshProfileModal] = useState(false);
    const [sshProfileInitialId, setSshProfileInitialId] = useState<number | null>(null);
    const [sshProfileApplySiteId, setSshProfileApplySiteId] = useState<number | null>(null);
    const [pathManageInitialId, setPathManageInitialId] = useState<number | null>(null);
    const [showDeploymentHistoryModal, setShowDeploymentHistoryModal] = useState(false);
    const [showIntroGuideModal, setShowIntroGuideModal] = useState<boolean>(false);
    const [updateInfo, setUpdateInfo] = useState<UpdateCheckResult | null>(null);
    const [showUpdateBanner, setShowUpdateBanner] = useState(false);
    const [isUpdateDownloading, setIsUpdateDownloading] = useState(false);
    const [isManagementMenuOpen, setIsManagementMenuOpen] = useState(false);
    const [isPathMenuOpen, setIsPathMenuOpen] = useState(false);
    const [isContactMenuOpen, setIsContactMenuOpen] = useState(false);
    const [workspacePage, setWorkspacePage] = useState<WorkspacePage>("package");
    const [isTerminalNavigatorOpen, setIsTerminalNavigatorOpen] = useState(false);
    const [sshTerminalSessions, setSshTerminalSessions] = useState<TerminalSession[]>([]);
    const [activeSshTerminalSessionId, setActiveSshTerminalSessionId] =
        useState<string | null>(null);
    const [isOpeningSshTerminal, setIsOpeningSshTerminal] = useState(false);
    const pathFileInputRef = useRef<HTMLInputElement>(null);
    const managementMenuRef = useRef<HTMLDivElement>(null);
    const pathMenuRef = useRef<HTMLDivElement>(null);
    const contactMenuRef = useRef<HTMLDivElement>(null);
    const sshTerminalSequenceRef = useRef(0);
    const localTerminalSequenceRef = useRef(0);

    const appendSshTerminalSession = useCallback(
        (session: Omit<SshTerminalSession, "clientId" | "title">) => {
            const sequence = ++sshTerminalSequenceRef.current;
            const nextSession: SshTerminalSession = {
                ...session,
                kind: "ssh",
                clientId: `ssh-${Date.now()}-${sequence}`,
                title: `${session.site.text || session.site.sshHost || "SSH"} ${sequence}`,
            };
            setSshTerminalSessions((current) => [...current, nextSession]);
            setActiveSshTerminalSessionId(nextSession.clientId);
            setSelected(session.site);
            setWorkspacePage("terminal");
        },
        []
    );

    const duplicateSshTerminalSession = useCallback(
        (sourceSessionId: string) => {
            const source = sshTerminalSessions.find(
                (session) => session.clientId === sourceSessionId
            );
            if (!source || source.kind === "local") return;
            appendSshTerminalSession({
                kind: "ssh",
                historyId: source.historyId,
                site: source.site,
                credentials: source.credentials,
                workingDirectory: source.workingDirectory,
                siteTerminalId: source.siteTerminalId,
            });
        },
        [appendSshTerminalSession, sshTerminalSessions]
    );

    const closeSshTerminalSession = useCallback(
        (sessionId: string) => {
            const closingIndex = sshTerminalSessions.findIndex(
                (session) => session.clientId === sessionId
            );
            if (closingIndex < 0) return;

            const remaining = sshTerminalSessions.filter(
                (session) => session.clientId !== sessionId
            );
            setSshTerminalSessions(remaining);
            if (activeSshTerminalSessionId === sessionId) {
                const nextIndex = Math.min(closingIndex, remaining.length - 1);
                const nextSession = nextIndex >= 0 ? remaining[nextIndex] : null;
                setActiveSshTerminalSessionId(nextSession?.clientId ?? null);
                if (nextSession && nextSession.kind !== "local") {
                    setSelected(
                        sites.find((site) => site.id === nextSession.site.id) ??
                            nextSession.site
                    );
                }
            }
        },
        [activeSshTerminalSessionId, sites, sshTerminalSessions]
    );

    const closeAllSshTerminalSessions = useCallback(() => {
        setSshTerminalSessions([]);
        setActiveSshTerminalSessionId(null);
    }, []);

    const openLocalTerminal = useCallback(() => {
        const sequence = ++localTerminalSequenceRef.current;
        const nextSession: TerminalSession = {
            kind: "local",
            clientId: `local-${Date.now()}-${sequence}`,
            title: `PowerShell ${sequence}`,
            workingDirectory: selected?.localPath?.trim() || "",
        };
        setSshTerminalSessions((current) => [...current, nextSession]);
        setActiveSshTerminalSessionId(nextSession.clientId);
        setWorkspacePage("terminal");
    }, [selected]);

    const openSshTerminalForSite = useCallback(async (site: Site) => {
        if (isOpeningSshTerminal) return;

        setIsOpeningSshTerminal(true);
        try {
            const credentials = await requestTransferCredentials(site);
            if (!credentials) return;

            appendSshTerminalSession({
                historyId: `site:${site.id}`,
                siteTerminalId: site.id,
                site,
                credentials,
                workingDirectory: site.remoteUploadPath?.trim() || ".",
            });
        } finally {
            setIsOpeningSshTerminal(false);
        }
    }, [appendSshTerminalSession, isOpeningSshTerminal]);

    const openSelectedSshTerminal = useCallback(() => {
        if (selected) void openSshTerminalForSite(selected);
    }, [openSshTerminalForSite, selected]);

    const activateSshTerminalSession = useCallback(
        (sessionId: string) => {
            const session = sshTerminalSessions.find(
                (item) => item.clientId === sessionId
            );
            if (!session) return;

            setActiveSshTerminalSessionId(sessionId);
            if (session.kind !== "local") {
                setSelected(
                    sites.find((site) => site.id === session.site.id) ?? session.site
                );
            }
        },
        [sites, sshTerminalSessions]
    );

    const selectDeploymentSite = useCallback(
        (siteId: number) => {
            const site = sites.find((item) => item.id === siteId) ?? null;
            setSelected(site);
            if (workspacePage !== "terminal" || !site) return;

            const existingSession = [...sshTerminalSessions]
                .reverse()
                .find(
                    (session) =>
                        session.kind !== "local" && session.site.id === site.id
                );
            if (existingSession) {
                setActiveSshTerminalSessionId(existingSession.clientId);
                return;
            }

            if (site.sshHost?.trim() && site.sshUsername?.trim()) {
                void openSshTerminalForSite(site);
            }
        },
        [
            openSshTerminalForSite,
            sites,
            sshTerminalSessions,
            workspacePage,
        ]
    );

    const fetchSites = useCallback(() => {
        serverApi
            .get<Site[]>("/api/sites")
            .then((res) => {
                setSites(res.data);
                setSelected((prev) => res.data.find((site) => site.id === prev?.id) ?? res.data[0] ?? null);
            })
            .catch((err) => console.error("site list load failed:", err));
    }, []);

    useEffect(() => {
        fetchSites();
    }, [fetchSites]);

    useEffect(() => {
        if (!sites.some((site) => site.compileCacheStatus === "BUILDING")) return;
        const timer = window.setInterval(fetchSites, 1500);
        return () => window.clearInterval(timer);
    }, [fetchSites, sites]);

    useEffect(() => {
        let cancelled = false;

        serverApi
            .get<IntroGuidePreferenceResponse>("/api/preferences/intro-guide")
            .then((response) => {
                if (cancelled) return;

                if (response.data.guideVersion === INTRO_GUIDE_CONTENT_VERSION) {
                    localStorage.setItem(INTRO_GUIDE_STORAGE_KEY, INTRO_GUIDE_CONTENT_VERSION);
                    return;
                }

                if (localStorage.getItem(INTRO_GUIDE_STORAGE_KEY) === INTRO_GUIDE_CONTENT_VERSION) {
                    void markIntroGuideVersionSeen();
                    return;
                }

                setShowIntroGuideModal(true);
            })
            .catch((error) => {
                console.info("intro guide preference load failed:", error);
                if (!cancelled && localStorage.getItem(INTRO_GUIDE_STORAGE_KEY) !== INTRO_GUIDE_CONTENT_VERSION) {
                    setShowIntroGuideModal(true);
                }
            });

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!isManagementMenuOpen) return;

        const handleMouseDown = (event: MouseEvent) => {
            if (!managementMenuRef.current?.contains(event.target as Node)) {
                setIsManagementMenuOpen(false);
            }
        };

        document.addEventListener("mousedown", handleMouseDown);
        return () => document.removeEventListener("mousedown", handleMouseDown);
    }, [isManagementMenuOpen]);

    useEffect(() => {
        if (!isPathMenuOpen) return;

        const handleMouseDown = (event: MouseEvent) => {
            if (!pathMenuRef.current?.contains(event.target as Node)) {
                setIsPathMenuOpen(false);
            }
        };

        document.addEventListener("mousedown", handleMouseDown);
        return () => document.removeEventListener("mousedown", handleMouseDown);
    }, [isPathMenuOpen]);

    useEffect(() => {
        if (!isContactMenuOpen) return;

        const handleMouseDown = (event: MouseEvent) => {
            if (!contactMenuRef.current?.contains(event.target as Node)) {
                setIsContactMenuOpen(false);
            }
        };

        document.addEventListener("mousedown", handleMouseDown);
        return () => document.removeEventListener("mousedown", handleMouseDown);
    }, [isContactMenuOpen]);

    useEffect(() => {
        let cancelled = false;
        const timer = window.setTimeout(() => {
            checkForAppUpdate().then((result) => {
                if (cancelled) return;
                setUpdateInfo(result);
                if (result) {
                    setShowUpdateBanner(
                        localStorage.getItem(UPDATE_DISMISSED_VERSION_STORAGE_KEY) !== result.latestVersion
                    );
                }
            });
        }, 3000);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, []);

    const closeIntroGuide = () => {
        void markIntroGuideVersionSeen();
        setShowIntroGuideModal(false);
    };

    const handlePathUpdateClose = () => {
        setShowPathUpdateModal(false);
        setPathManageInitialId(null);
        setPathManageCreateMode(false);
        fetchSites();
    };

    const openPathManage = (siteId?: number, createMode = false) => {
        setPathManageInitialId(siteId ?? null);
        setPathManageCreateMode(createMode);
        setShowPathUpdateModal(true);
    };

    const openSshProfileManager = (profileId?: number, applySiteId?: number) => {
        setSshProfileInitialId(profileId ?? null);
        setSshProfileApplySiteId(applySiteId ?? null);
        setShowSshProfileModal(true);
    };

    const closeSshProfileManager = () => {
        const returnSiteId = sshProfileApplySiteId;
        setShowSshProfileModal(false);
        setSshProfileInitialId(null);
        setSshProfileApplySiteId(null);
        fetchSites();
        if (returnSiteId != null) {
            setPathManageInitialId(returnSiteId);
            setPathManageCreateMode(false);
            setShowPathUpdateModal(true);
        }
    };

    const reorderSites = useCallback(async (siteIds: number[]) => {
        try {
            const reorderedSites = await updateSiteOrder(siteIds);
            setSites(reorderedSites);
            setSelected((current) =>
                reorderedSites.find((site) => site.id === current?.id) ?? reorderedSites[0] ?? null
            );
        } catch (error) {
            console.error("site order save failed:", error);
            fetchSites();
            await Swal.fire({
                icon: "error",
                title: "경로 순서 저장 실패",
                text: "목록을 다시 불러왔습니다. 잠시 후 다시 시도해 주세요.",
            });
            throw error;
        }
    }, [fetchSites]);

    const handleUpdateClick = async () => {
        if (!updateInfo?.installerUrl || isUpdateDownloading) return;

        setIsUpdateDownloading(true);
        Swal.fire({
            title: "업데이트 파일 다운로드 중",
            text: "최신 deployKit 설치 파일을 다운로드 폴더에 저장하고 있습니다.",
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => Swal.showLoading(),
        });

        try {
            const result = await downloadAppUpdateInstaller(updateInfo.installerUrl, updateInfo.latestVersion);
            if (result.launched) {
                await Swal.fire({
                    icon: "success",
                    title: "업데이트 설치를 시작합니다.",
                    text: "deployKit을 종료한 뒤 설치가 자동으로 진행됩니다. 권한 확인 창이 뜨면 허용해 주세요.",
                    timer: 2200,
                    showConfirmButton: false,
                });
                await requestLocalAppShutdown();
                return;
            }

            await Swal.fire({
                icon: "success",
                title: "업데이트 파일을 다운로드했습니다.",
                text: `${result.fileName}을 다운로드 폴더에 저장했습니다. 열린 폴더에서 설치 파일을 실행해 주세요.`,
                confirmButtonText: "확인",
            });
        } catch (error) {
            console.info("download update installer through desktop API failed:", error);
            await Swal.fire({
                icon: "error",
                title: "업데이트 다운로드 실패",
                text: updateDownloadErrorMessage(error),
                confirmButtonText: "확인",
            });
        } finally {
            setIsUpdateDownloading(false);
        }
    };

    const dismissUpdateBanner = () => {
        if (updateInfo?.latestVersion) {
            localStorage.setItem(UPDATE_DISMISSED_VERSION_STORAGE_KEY, updateInfo.latestVersion);
        }
        setShowUpdateBanner(false);
    };

    const downloadPaths = async () => {
        try {
            const response = await serverApi.get<Site[]>("/api/pathList");
            const pathList = response.data;
            setSites(pathList);
            setSelected((prev) => pathList.find((site) => site.id === prev?.id) ?? pathList[0] ?? null);

            if (pathList.length === 0) {
                await Swal.fire({
                    icon: "info",
                    title: "다운로드할 경로가 없습니다.",
                    text: "먼저 경로를 등록해 주세요.",
                });
                return;
            }

            const backup = {
                app: "DeployProject",
                schemaVersion: 3,
                exportedAt: new Date().toISOString(),
                sites: pathList.map(toPathBackupSite),
            };
            const blob = new Blob([JSON.stringify(backup, null, 2)], {
                type: "application/json;charset=utf-8",
            });
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = `deploy-project-paths-${timestampForFileName()}.json`;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("path download failed", error);
            await Swal.fire({
                icon: "error",
                title: "경로 다운로드 실패",
                text: "저장된 경로 목록을 불러오지 못했습니다.",
            });
        }
    };

    const importPaths = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;

        try {
            const importedSites = uniquePathSites(parseImportedPaths(JSON.parse(await file.text())));

            if (importedSites.length === 0) {
                await Swal.fire({
                    icon: "warning",
                    title: "등록할 경로가 없습니다.",
                    text: "파일에 사이트 이름, 배포 서버 경로, 로컬 프로젝트 경로가 포함되어 있는지 확인해 주세요.",
                });
                return;
            }

            const currentResponse = await serverApi.get<Site[]>("/api/pathList");
            const existingKeys = new Set(currentResponse.data.map(pathIdentity));
            const sitesToImport = importedSites.filter((site) => !existingKeys.has(pathIdentity(site)));
            const skippedCount = importedSites.length - sitesToImport.length;

            if (sitesToImport.length === 0) {
                await Swal.fire({
                    icon: "info",
                    title: "추가할 새 경로가 없습니다.",
                    text: `${skippedCount}개의 경로가 이미 등록되어 있습니다.`,
                });
                return;
            }

            const result = await Swal.fire({
                icon: "question",
                title: "경로를 가져올까요?",
                text:
                    skippedCount > 0
                        ? `${sitesToImport.length}개의 경로를 추가하고, 중복 ${skippedCount}개는 제외합니다.`
                        : `${sitesToImport.length}개의 경로를 현재 목록에 추가합니다.`,
                showCancelButton: true,
                confirmButtonText: "가져오기",
                cancelButtonText: "취소",
            });

            if (!result.isConfirmed) return;

            const preserveImportedSort = currentResponse.data.length === 0;
            for (const site of sitesToImport) {
                await serverApi.post("/api/savedPath", {
                    ...site,
                    sort: preserveImportedSort ? site.sort : undefined,
                });
            }

            fetchSites();
            await Swal.fire({
                position: "center",
                icon: "success",
                title:
                    skippedCount > 0
                        ? `경로 ${sitesToImport.length}개를 가져왔습니다.`
                        : "경로를 가져왔습니다.",
                timer: 1200,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error("path import failed", error);
            await Swal.fire({
                icon: "error",
                title: "경로 가져오기 실패",
                text: "JSON 파일 형식이 올바른지 확인해 주세요.",
            });
        }
    };

    const exportErrorLogs = async () => {
        setIsContactMenuOpen(false);

        Swal.fire({
            title: "에러 로그 추출 중",
            text: "로그 파일을 ZIP으로 묶어 Downloads 폴더에 저장하고 있습니다.",
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => Swal.showLoading(),
        });

        try {
            const response = await serverApi.post<SupportLogExportResponse>("/api/support/logs/export");
            const { path, fileName, logFileCount } = response.data;

            await Swal.fire({
                icon: "success",
                title: "에러 로그 추출 완료",
                text: `${path || fileName} (${logFileCount}개 로그 포함)`,
                confirmButtonText: "확인",
            });
        } catch (error) {
            console.error("support log export failed", error);
            await Swal.fire({
                icon: "error",
                title: "에러 로그 추출 실패",
                text: "로그 파일을 생성하지 못했습니다. 앱을 다시 실행한 뒤 재시도해 주세요.",
            });
        }
    };

    const selectedHasSsh = Boolean(
        selected?.sshHost?.trim() && selected?.sshUsername?.trim()
    );

    return (
        <div style={styles.wrapper} className={isPathMenuOpen ? "path-menu-open" : undefined}>
            <div style={styles.container}>
                <IntroGuideModal show={showIntroGuideModal} onClose={closeIntroGuide} />
                <PathUpdateModal
                    show={showPathUpdateModal}
                    onPath={handlePathUpdateClose}
                    initialPathId={pathManageInitialId}
                    initialCreateMode={pathManageCreateMode}
                    onOpenSshProfiles={(profileId, siteId) => {
                        setShowPathUpdateModal(false);
                        openSshProfileManager(profileId, siteId);
                    }}
                />
                <SshProfileManagerModal
                    show={showSshProfileModal}
                    initialProfileId={sshProfileInitialId}
                    onClose={closeSshProfileManager}
                    focusSiteId={sshProfileApplySiteId}
                />
                <DeploymentHistoryModal
                    show={showDeploymentHistoryModal}
                    sites={sites}
                    defaultSiteId={selected?.id}
                    onClose={() => setShowDeploymentHistoryModal(false)}
                />

                <div style={styles.converterHeader}>
                    <div className="brand-lockup">
                        <img className="brand-logo-image" src={deployKitIcon} alt="" aria-hidden="true" />
                        <div className="brand-copy">
                            <div className="brand-title-row">
                                <h1 style={styles.converterHeaderTitle}>DeployKit</h1>
                                <span className="brand-version">v{appVersion}</span>
                                {updateInfo && showUpdateBanner && (
                                    <div className="brand-update-notice">
                                        <button
                                            type="button"
                                            className="brand-update-action"
                                            disabled={isUpdateDownloading}
                                            aria-label={`v${updateInfo.latestVersion} 업데이트 다운로드`}
                                            title={
                                                updateInfo.message
                                                    ?? `v${updateInfo.latestVersion} 업데이트 다운로드`
                                            }
                                            onClick={handleUpdateClick}
                                        >
                                            <span aria-hidden="true" />
                                            {isUpdateDownloading
                                                ? "다운로드 중"
                                                : `v${updateInfo.latestVersion} 업데이트`}
                                        </button>
                                        <button
                                            type="button"
                                            className="brand-update-dismiss"
                                            aria-label="업데이트 알림 닫기"
                                            title="이번 버전 알림 숨기기"
                                            onClick={dismissUpdateBanner}
                                        >
                                            ×
                                        </button>
                                    </div>
                                )}
                            </div>
                            <p className="brand-subtitle">Build. Configure. Deploy.</p>
                        </div>
                    </div>
                </div>

                <div className="app-top-navigation">
                    <nav className="app-workspace-tabs" role="tablist" aria-label="작업 화면">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={workspacePage === "package"}
                            className={workspacePage === "package" ? "is-active" : undefined}
                            onClick={() => setWorkspacePage("package")}
                        >
                            패키징
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={workspacePage === "terminal"}
                            className={workspacePage === "terminal" ? "is-active" : undefined}
                            onClick={() => {
                                setWorkspacePage("terminal");
                                if (activeSshTerminalSessionId) {
                                    activateSshTerminalSession(activeSshTerminalSessionId);
                                }
                            }}
                        >
                            터미널
                            {sshTerminalSessions.length > 0 && (
                                <span>{sshTerminalSessions.length}</span>
                            )}
                        </button>
                    </nav>

                    <div className="app-management-tabs" aria-label="관리 메뉴">
                        <div className="header-menu app-tools-menu app-management-menu" ref={managementMenuRef}>
                            <button
                                type="button"
                                className="app-tools-trigger"
                                aria-label="경로·SSH 관리 메뉴"
                                aria-haspopup="menu"
                                aria-expanded={isManagementMenuOpen}
                                onClick={() => {
                                    setIsManagementMenuOpen((current) => !current);
                                    setIsPathMenuOpen(false);
                                }}
                            >
                                경로·SSH 관리
                                <span aria-hidden="true" />
                            </button>
                            {isManagementMenuOpen && (
                                <div className="header-menu-panel" role="menu">
                                    <button
                                        type="button"
                                        className="header-menu-item"
                                        role="menuitem"
                                        onClick={() => {
                                            setIsManagementMenuOpen(false);
                                            openPathManage();
                                        }}
                                    >
                                        <strong>경로 관리</strong>
                                        <small>경로 등록·수정·삭제</small>
                                    </button>
                                    <button
                                        type="button"
                                        className="header-menu-item"
                                        role="menuitem"
                                        onClick={() => {
                                            setIsManagementMenuOpen(false);
                                            openSshProfileManager();
                                        }}
                                    >
                                        <strong>SSH 관리</strong>
                                        <small>접속 프로필 관리</small>
                                    </button>
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={() => setShowDeploymentHistoryModal(true)}
                        >
                            배포 이력
                        </button>
                        <div className="header-menu app-tools-menu" ref={pathMenuRef}>
                            <button
                                type="button"
                                className="app-tools-trigger"
                                aria-label="도구 메뉴"
                                aria-haspopup="menu"
                                aria-expanded={isPathMenuOpen}
                                onClick={() => {
                                    setIsPathMenuOpen((prev) => !prev);
                                    setIsManagementMenuOpen(false);
                                }}
                            >
                                도구
                                <span aria-hidden="true" />
                            </button>
                            {isPathMenuOpen && (
                                <div className="header-menu-panel" role="menu">
                                    <button
                                        type="button"
                                        className="header-menu-item"
                                        role="menuitem"
                                        onClick={() => {
                                            setIsPathMenuOpen(false);
                                            downloadPaths();
                                        }}
                                    >
                                        <strong>경로 백업</strong>
                                        <small>JSON 파일로 저장</small>
                                    </button>
                                    <button
                                        type="button"
                                        className="header-menu-item"
                                        role="menuitem"
                                        onClick={() => {
                                            setIsPathMenuOpen(false);
                                            pathFileInputRef.current?.click();
                                        }}
                                    >
                                        <strong>경로 가져오기</strong>
                                        <small>백업 JSON 불러오기</small>
                                    </button>
                                    <button
                                        type="button"
                                        className="header-menu-item"
                                        role="menuitem"
                                        onClick={() => {
                                            setIsPathMenuOpen(false);
                                            exportErrorLogs();
                                        }}
                                    >
                                        <strong>에러 로그 추출</strong>
                                        <small>문의용 ZIP 파일 저장</small>
                                    </button>
                                </div>
                            )}
                        </div>
                        <span className="app-management-divider" aria-hidden="true" />
                        <button
                            type="button"
                            className="theme-toggle app-nav-theme"
                            aria-pressed={themeMode === "dark"}
                            onClick={onToggleThemeMode}
                        >
                            <span className="theme-toggle-track" aria-hidden="true">
                                <span className="theme-toggle-thumb" />
                            </span>
                            <span>{themeMode === "dark" ? "다크" : "라이트"}</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowIntroGuideModal(true)}
                        >
                            사용 안내
                        </button>
                        <div className="app-contact-menu" ref={contactMenuRef}>
                            <button
                                type="button"
                                className="app-contact-trigger"
                                aria-haspopup="menu"
                                aria-expanded={isContactMenuOpen}
                                onClick={() => setIsContactMenuOpen((prev) => !prev)}
                            >
                                문의하기
                            </button>
                            {isContactMenuOpen && (
                                <div className="app-contact-panel" role="menu">
                                    <a
                                        className="app-contact-link"
                                        href={SUPPORT_GITHUB_URL}
                                        target="_blank"
                                        rel="noreferrer"
                                        role="menuitem"
                                    >
                                        GitHub
                                    </a>
                                    <a
                                        className="app-contact-link"
                                        href={`mailto:${SUPPORT_EMAIL}`}
                                        role="menuitem"
                                    >
                                        Email
                                    </a>
                                    <button
                                        type="button"
                                        className="app-contact-link app-contact-button"
                                        role="menuitem"
                                        onClick={exportErrorLogs}
                                    >
                                        에러 로그 추출
                                    </button>
                                </div>
                            )}
                        </div>
                        <input
                            ref={pathFileInputRef}
                            type="file"
                            accept="application/json,.json"
                            style={{ display: "none" }}
                            onChange={importPaths}
                        />
                    </div>
                </div>

                <div
                    className={`app-workspace-layout${
                        workspacePage === "terminal" ? " is-terminal" : ""
                    }${
                        workspacePage === "terminal" &&
                        sshTerminalSessions.length > 0 &&
                        !isTerminalNavigatorOpen
                            ? " is-navigator-collapsed"
                            : ""
                    }`}
                >
                    <SiteNavigator
                        sites={sites}
                        selectedId={selected?.id ?? null}
                        onSelect={selectDeploymentSite}
                        onAdd={() => openPathManage(undefined, true)}
                        onManage={openPathManage}
                        onManageSsh={() => openSshProfileManager()}
                        onReorder={reorderSites}
                    />

                    <main
                        className={`app-workspace-main${
                            workspacePage === "terminal" ? " is-terminal" : ""
                        }`}
                    >
                        {workspacePage === "package" ? (
                            <>
                                {selected && (
                                    <div style={styles.customCardPath}>
                                        <PathConverter
                                            site={selected}
                                            suspendScrollbars={isPathMenuOpen}
                                            onSshSessionOpen={appendSshTerminalSession}
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="dp-ssh-page-shell">
                                {sshTerminalSessions.length > 0 ? (
                                    <SshTerminalWorkspace
                                        sessions={sshTerminalSessions}
                                        activeSessionId={activeSshTerminalSessionId}
                                        sites={sites}
                                        selectedSite={selected}
                                        onSelect={activateSshTerminalSession}
                                        onDuplicate={duplicateSshTerminalSession}
                                        onOpenSite={(site) => void openSshTerminalForSite(site)}
                                        onOpenLocal={openLocalTerminal}
                                        onClose={closeSshTerminalSession}
                                        onCloseAll={closeAllSshTerminalSessions}
                                        isNavigatorOpen={isTerminalNavigatorOpen}
                                        onToggleNavigator={() =>
                                            setIsTerminalNavigatorOpen((current) => !current)
                                        }
                                    />
                                ) : (
                                    <div className="dp-ssh-page-empty">
                                        {selected && (
                                            <div className="dp-ssh-page-empty-target">
                                                <span
                                                    className={selectedHasSsh ? "is-ready" : undefined}
                                                    aria-hidden="true"
                                                />
                                                <div>
                                                    <strong>{selected.text}</strong>
                                                    <small>
                                                        {selectedHasSsh
                                                            ? `${selected.sshUsername}@${selected.sshHost}:${selected.sshPort ?? 22}`
                                                            : "SSH 연결 정보가 없습니다."}
                                                    </small>
                                                </div>
                                            </div>
                                        )}
                                        <strong>열린 터미널이 없습니다.</strong>
                                        <p>
                                            {selectedHasSsh
                                                ? "선택한 배포 경로의 SSH 또는 로컬 터미널을 열 수 있습니다."
                                                : "로컬 터미널을 열거나 SSH 연결 정보를 등록해 주세요."}
                                        </p>
                                        <div className="dp-ssh-page-empty-actions">
                                            {selectedHasSsh && (
                                                <button
                                                    type="button"
                                                    className="is-primary"
                                                    disabled={isOpeningSshTerminal}
                                                    onClick={openSelectedSshTerminal}
                                                >
                                                    {isOpeningSshTerminal
                                                        ? "인증 정보 확인 중"
                                                        : "SSH 터미널 열기"}
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className={selectedHasSsh ? undefined : "is-primary"}
                                                onClick={openLocalTerminal}
                                            >
                                                로컬 PowerShell
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setWorkspacePage("package")}
                                            >
                                                {selectedHasSsh ? "패키징으로 이동" : "SSH 설정으로 이동"}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </main>
                </div>

            </div>
        </div>
    );
};

export default ConverterMain;
